/**
 * Illustrative sample data — ported 1:1 from the design reference
 * (docs/design-reference/project/NicheLens.dc.html, buildData()).
 *
 * This is the "Illustrative sample data" the design ships with. The app is
 * sample-data-first: the API client falls back to this so the UI always renders.
 */

/** Palette constant `C` — verbatim from the design (the inline hex values win). */
export const C = {
  ink: '#303030',
  ink2: '#616161',
  ink3: '#6d7175',
  faint: '#b4b9c2',
  primary: '#2c6ecb',
  primarySoft: '#eef0f2',
  brand: '#2c6ecb',
  open: '#008060',
  openBg: '#e3f1df',
  openBar: '#29845a',
  contested: '#b98900',
  contestedBg: '#fcf1cd',
  contestedBar: '#e8a33d',
  sat: '#d72c0d',
  satBg: '#fdeae7',
  satBar: '#e0301e',
} as const;

export type Momentum = 'rising' | 'falling' | 'flat';
export type FunctionStatus = 'won' | 'contested' | 'open';

/**
 * Marketplaces ScanGap indexes. The product is platform-agnostic; each niche
 * belongs to exactly one marketplace (Shopify niches ≠ monday.com niches), and
 * the top-bar switcher filters the Explorer by `slug`. New marketplaces are
 * added here + in the seed — no schema change. See docs/prd.md §5.
 */
export type PlatformSlug = 'shopify' | 'wix' | 'monday' | 'jira' | 'zendesk';

export interface Platform {
  slug: PlatformSlug;
  name: string;
  /** Letter shown in the square badge. */
  initial: string;
  /** Badge background — a brand accent, NOT an opportunity verdict hue. */
  badgeBg: string;
  /** Representative niche, shown as the switcher subtitle. */
  tagline: string;
}

export const PLATFORMS: Platform[] = [
  { slug: 'shopify', name: 'Shopify', initial: 'S', badgeBg: '#008060', tagline: 'Reviews & UGC' },
  { slug: 'wix', name: 'Wix', initial: 'W', badgeBg: '#2c6ecb', tagline: 'Bookings & Scheduling' },
  {
    slug: 'monday',
    name: 'monday.com',
    initial: 'M',
    badgeBg: '#7b61ff',
    tagline: 'Time Tracking',
  },
  {
    slug: 'jira',
    name: 'Jira',
    initial: 'J',
    badgeBg: '#2563eb',
    tagline: 'Time Tracking & Worklogs',
  },
  {
    slug: 'zendesk',
    name: 'Zendesk',
    initial: 'Z',
    badgeBg: '#1f2a24',
    tagline: 'AI & Automation',
  },
];

export const DEFAULT_PLATFORM: PlatformSlug = 'shopify';

export function platformMeta(slug: PlatformSlug): Platform {
  return PLATFORMS.find((p) => p.slug === slug) ?? PLATFORMS[0]!;
}

export interface Niche {
  id: string;
  platform: PlatformSlug;
  name: string;
  icon: string;
  appsReal: number;
  appsAnalyzed?: number;
  rating: number;
  sat: number;
  mom: Momentum;
  ready?: boolean;
  iconBg: string;
}

export interface OppScore {
  score: number;
  demand: number;
  gap: number;
  monet: number;
}

export interface NicheFunction {
  id: string;
  name: string;
  status: FunctionStatus;
  order: number;
  definition: string;
  statusWhy: string;
  incumbent: string | null;
  opp: OppScore;
}

export interface App {
  id: string;
  name: string;
  vendor: string;
  reviews: number;
  rating: number;
  mom: Momentum;
  depths: number[];
  iconBg: string;
  initials: string;
  scores: (number | null)[];
  gated: boolean[];
}

export interface OppEvidenceEntry {
  complaints: { text: string; count: number }[];
  gatedBy: { text: string; count?: number }[];
}

export interface UnmetDemand {
  label: string;
  count: number;
  trend: Momentum;
}

export interface SampleData {
  niches: Niche[];
  functions: NicheFunction[];
  apps: App[];
  gatedFns: Set<number>;
  oppEvidence: Record<string, OppEvidenceEntry>;
  unmet: UnmetDemand[];
}

export function buildData(): SampleData {
  // prettier-ignore
  const nicheDefs: Omit<Niche, 'iconBg'>[] = [
    // ---- Shopify (the fully-analyzed demo niche is reviews-ugc) ----
    { id: 'reviews-ugc', platform: 'shopify', name: 'Reviews & UGC', icon: '★', appsReal: 240, appsAnalyzed: 10, rating: 4.85, sat: 71, mom: 'flat', ready: true },
    { id: 'upsell', platform: 'shopify', name: 'Upsell & Cross-sell', icon: '↗', appsReal: 180, rating: 4.7, sat: 64, mom: 'rising' },
    { id: 'loyalty', platform: 'shopify', name: 'Loyalty & Rewards', icon: '◈', appsReal: 150, rating: 4.6, sat: 58, mom: 'flat' },
    { id: 'subscriptions', platform: 'shopify', name: 'Subscriptions', icon: '↻', appsReal: 95, rating: 4.5, sat: 47, mom: 'rising' },
    { id: 'wishlist', platform: 'shopify', name: 'Wishlist', icon: '♡', appsReal: 60, rating: 4.4, sat: 33, mom: 'rising' },
    { id: 'bis', platform: 'shopify', name: 'Back-in-Stock Alerts', icon: '◉', appsReal: 70, rating: 4.6, sat: 41, mom: 'flat' },
    { id: 'pagebuilder', platform: 'shopify', name: 'Page Builders', icon: '▤', appsReal: 130, rating: 4.8, sat: 76, mom: 'falling' },
    { id: 'affiliate', platform: 'shopify', name: 'Affiliate & Referral', icon: '⤳', appsReal: 110, rating: 4.5, sat: 52, mom: 'rising' },
    { id: 'preorder', platform: 'shopify', name: 'Pre-order & Waitlist', icon: '◷', appsReal: 45, rating: 4.3, sat: 29, mom: 'rising' },
    { id: 'seo', platform: 'shopify', name: 'SEO & Speed', icon: '⚡', appsReal: 200, rating: 4.6, sat: 68, mom: 'flat' },

    // ---- Wix App Market ----
    { id: 'wix-bookings', platform: 'wix', name: 'Bookings & Scheduling', icon: '◴', appsReal: 90, rating: 4.5, sat: 55, mom: 'rising' },
    { id: 'wix-marketing', platform: 'wix', name: 'Email Marketing', icon: '✉', appsReal: 110, rating: 4.3, sat: 62, mom: 'rising' },
    { id: 'wix-forms', platform: 'wix', name: 'Forms & Surveys', icon: '▤', appsReal: 70, rating: 4.4, sat: 48, mom: 'flat' },
    { id: 'wix-members', platform: 'wix', name: 'Memberships', icon: '◈', appsReal: 60, rating: 4.4, sat: 41, mom: 'rising' },
    { id: 'wix-events', platform: 'wix', name: 'Events & Ticketing', icon: '◉', appsReal: 45, rating: 4.5, sat: 33, mom: 'flat' },

    // ---- monday.com apps marketplace ----
    { id: 'monday-time', platform: 'monday', name: 'Time Tracking', icon: '◷', appsReal: 40, rating: 4.6, sat: 44, mom: 'rising' },
    { id: 'monday-dashboards', platform: 'monday', name: 'Reporting & Dashboards', icon: '▦', appsReal: 55, rating: 4.5, sat: 57, mom: 'flat' },
    { id: 'monday-integrations', platform: 'monday', name: 'Integrations & Sync', icon: '↻', appsReal: 70, rating: 4.3, sat: 64, mom: 'flat' },
    { id: 'monday-crm', platform: 'monday', name: 'CRM & Sales', icon: '⤳', appsReal: 35, rating: 4.4, sat: 38, mom: 'rising' },
    { id: 'monday-automations', platform: 'monday', name: 'Automations', icon: '⚡', appsReal: 30, rating: 4.6, sat: 29, mom: 'rising' },

    // ---- Atlassian / Jira marketplace ----
    { id: 'jira-worklogs', platform: 'jira', name: 'Time Tracking & Worklogs', icon: '◷', appsReal: 60, rating: 4.4, sat: 58, mom: 'flat' },
    { id: 'jira-reports', platform: 'jira', name: 'Reports & Analytics', icon: '▦', appsReal: 80, rating: 4.3, sat: 66, mom: 'falling' },
    { id: 'jira-testing', platform: 'jira', name: 'Test Management', icon: '◎', appsReal: 40, rating: 4.5, sat: 41, mom: 'rising' },
    { id: 'jira-workflow', platform: 'jira', name: 'Workflow & Automation', icon: '⚙', appsReal: 90, rating: 4.2, sat: 71, mom: 'flat' },
    { id: 'jira-diagrams', platform: 'jira', name: 'Diagrams & Charts', icon: '◭', appsReal: 35, rating: 4.6, sat: 33, mom: 'rising' },

    // ---- Zendesk marketplace ----
    { id: 'zendesk-ai', platform: 'zendesk', name: 'AI & Automation', icon: '✦', appsReal: 50, rating: 4.5, sat: 47, mom: 'rising' },
    { id: 'zendesk-telephony', platform: 'zendesk', name: 'Telephony & Voice', icon: '☎', appsReal: 40, rating: 4.3, sat: 52, mom: 'flat' },
    { id: 'zendesk-productivity', platform: 'zendesk', name: 'Agent Productivity', icon: '◧', appsReal: 70, rating: 4.4, sat: 61, mom: 'flat' },
    { id: 'zendesk-analytics', platform: 'zendesk', name: 'Analytics & QA', icon: '▦', appsReal: 55, rating: 4.5, sat: 49, mom: 'rising' },
    { id: 'zendesk-surveys', platform: 'zendesk', name: 'Surveys & CSAT', icon: '◔', appsReal: 30, rating: 4.4, sat: 31, mom: 'rising' },
  ];
  const niches: Niche[] = nicheDefs.map((n) => ({ ...n, iconBg: '#eef0f2' }));

  // functions for reviews-ugc (the canonical taxonomy = IP)
  const fnDefs: Omit<NicheFunction, 'opp'>[] = [
    {
      id: 'photovideo',
      name: 'Photo & Video Reviews',
      status: 'won',
      order: 0,
      definition:
        'Collect and display customer-submitted photo/video reviews, with moderation and load-speed optimization.',
      statusWhy:
        'Won: Loox & Judge.me cover it well at scale; reviews praise exactly this feature.',
      incumbent: 'Loox',
    },
    {
      id: 'reqauto',
      name: 'Review Request Automation',
      status: 'won',
      order: 1,
      definition:
        'Automatically send review-request emails/SMS after delivery, with scheduling and reminders.',
      statusWhy: 'Won: most incumbents do this well; hard to differentiate.',
      incumbent: 'Judge.me',
    },
    {
      id: 'widgets',
      name: 'On-site Widgets & Display',
      status: 'contested',
      order: 2,
      definition:
        'Widgets that display reviews on the PDP, homepage, carousels, and star badges — with UI customization.',
      statusWhy: 'Contested: many apps cover it; no one clearly leads on customization.',
      incumbent: null,
    },
    {
      id: 'qa',
      name: 'Q&A / Community',
      status: 'open',
      order: 3,
      definition: 'Let customers ask and answer publicly on product pages; build a knowledge base.',
      statusWhy: 'Open: very few apps do it, high demand in reviews — a pure opportunity.',
      incumbent: null,
    },
    {
      id: 'syndication',
      name: 'Review Syndication',
      status: 'contested',
      order: 4,
      definition:
        'Push reviews to Google Shopping, Meta, and rich snippets to boost off-site visibility.',
      statusWhy: 'Contested: Yotpo/Okendo do it well but gate it behind higher tiers.',
      incumbent: null,
    },
    {
      id: 'aimod',
      name: 'AI Moderation & Reply',
      status: 'open',
      order: 5,
      definition: 'Auto-filter spam/fake reviews and draft on-brand replies with AI.',
      statusWhy: 'Open: just emerging, no app does it deeply — demand is rising fast.',
      incumbent: null,
    },
    {
      id: 'ugcgallery',
      name: 'UGC Galleries / Shoppable',
      status: 'open',
      order: 6,
      definition:
        'Turn customer photos/videos into shoppable galleries embedded on-site, pulled from social.',
      statusWhy: 'Open: a few apps touch it, but quality is thin.',
      incumbent: null,
    },
    {
      id: 'import',
      name: 'Import Reviews',
      status: 'contested',
      order: 7,
      definition:
        'Import reviews from AliExpress, Amazon, CSV — for new stores that need to “seed” reviews.',
      statusWhy: 'Contested: Ali Reviews/Opinew specialize in it and compete on price.',
      incumbent: 'Ali Reviews',
    },
    {
      id: 'analytics',
      name: 'Insights & Analytics',
      status: 'open',
      order: 8,
      definition:
        'A dashboard analyzing sentiment, complaint themes, and review impact on conversion.',
      statusWhy: 'Open: most stop at star counts, lacking deep analysis — monetizable.',
      incumbent: null,
    },
  ];

  // apps sorted by review count desc (install proxy)
  const appDefs: Pick<App, 'id' | 'name' | 'vendor' | 'reviews' | 'rating' | 'mom' | 'depths'>[] = [
    {
      id: 'judgeme',
      name: 'Judge.me',
      vendor: 'Judge.me',
      reviews: 38420,
      rating: 4.9,
      mom: 'rising',
      depths: [3, 3, 3, 1, 2, 1, 1, 2, 2],
    },
    {
      id: 'loox',
      name: 'Loox',
      vendor: 'Loox',
      reviews: 22100,
      rating: 4.9,
      mom: 'rising',
      depths: [3, 3, 3, 0, 2, 1, 2, 1, 2],
    },
    {
      id: 'alireviews',
      name: 'Ali Reviews',
      vendor: 'Channelwill',
      reviews: 13800,
      rating: 4.9,
      mom: 'flat',
      depths: [2, 1, 2, 0, 1, 0, 1, 3, 1],
    },
    {
      id: 'yotpo',
      name: 'Yotpo',
      vendor: 'Yotpo',
      reviews: 7300,
      rating: 4.8,
      mom: 'flat',
      depths: [3, 3, 2, 2, 3, 2, 2, 1, 3],
    },
    {
      id: 'stamped',
      name: 'Stamped',
      vendor: 'Stamped.io',
      reviews: 6050,
      rating: 4.7,
      mom: 'falling',
      depths: [2, 2, 2, 2, 2, 1, 1, 2, 2],
    },
    {
      id: 'fera',
      name: 'Fera',
      vendor: 'Fera.ai',
      reviews: 5400,
      rating: 4.9,
      mom: 'rising',
      depths: [3, 2, 3, 0, 1, 2, 2, 1, 2],
    },
    {
      id: 'trustoo',
      name: 'Trustoo',
      vendor: 'Trustoo.io',
      reviews: 4200,
      rating: 4.9,
      mom: 'rising',
      depths: [2, 2, 2, 0, 1, 1, 1, 2, 1],
    },
    {
      id: 'rivyo',
      name: 'Rivyo',
      vendor: 'Thimatic',
      reviews: 2600,
      rating: 4.8,
      mom: 'flat',
      depths: [2, 1, 2, 1, 1, 0, 1, 2, 1],
    },
    {
      id: 'opinew',
      name: 'Opinew',
      vendor: 'Opinew',
      reviews: 1900,
      rating: 4.9,
      mom: 'flat',
      depths: [3, 2, 2, 0, 1, 1, 1, 3, 1],
    },
    {
      id: 'okendo',
      name: 'Okendo',
      vendor: 'Okendo',
      reviews: 1150,
      rating: 4.9,
      mom: 'rising',
      depths: [3, 3, 3, 2, 2, 2, 2, 1, 3],
    },
  ];

  const gatedFns = new Set<number>([4, 5, 6, 8]); // syndication, aimod, ugcgallery, analytics

  // quality score per cell (deterministic)
  const score = (d: number, ai: number, fi: number): number | null => {
    if (d === 0) return null;
    const h = (ai * 7 + fi * 13) % 11;
    return Math.min(99, Math.max(34, d * 24 + h));
  };

  const apps: App[] = appDefs.map((a, ai) => {
    const scores = a.depths.map((d, fi) => score(d, ai, fi));
    const gated = a.depths.map((d, fi) => d >= 2 && gatedFns.has(fi));
    return {
      ...a,
      iconBg: '#eef0f2',
      initials: a.name
        .replace(/[^A-Za-z]/g, '')
        .slice(0, 2)
        .toUpperCase(),
      scores,
      gated,
    };
  });

  // opportunities (function-level computed)
  const opp: Record<string, OppScore> = {
    qa: { score: 88, demand: 84, gap: 90, monet: 72 },
    aimod: { score: 84, demand: 76, gap: 85, monet: 80 },
    analytics: { score: 79, demand: 70, gap: 74, monet: 88 },
    ugcgallery: { score: 73, demand: 80, gap: 70, monet: 62 },
    syndication: { score: 54, demand: 60, gap: 45, monet: 70 },
    import: { score: 41, demand: 55, gap: 35, monet: 42 },
    widgets: { score: 38, demand: 50, gap: 30, monet: 48 },
    reqauto: { score: 28, demand: 45, gap: 18, monet: 55 },
    photovideo: { score: 22, demand: 60, gap: 12, monet: 50 },
  };
  const functions: NicheFunction[] = fnDefs.map((f) => ({
    ...f,
    opp: opp[f.id] ?? { score: 0, demand: 0, gap: 0, monet: 0 },
  }));

  const oppEvidence: Record<string, OppEvidenceEntry> = {
    qa: {
      complaints: [
        { text: 'Customers ask about size/material but have no public place to ask', count: 62 },
        { text: 'The same questions get answered over and over in chat', count: 38 },
      ],
      gatedBy: [
        { text: 'No review app gates Q&A — you need a separate app' },
        { text: 'Dedicated Q&A apps charge $19–39/mo separately' },
      ],
    },
    aimod: {
      complaints: [
        { text: 'Spam/fake reviews slip through and must be deleted by hand', count: 54 },
        { text: 'Can’t reply to negative reviews on-brand fast enough', count: 41 },
      ],
      gatedBy: [
        { text: 'Yotpo gates auto-reply on the Pro plan', count: 0 },
        { text: 'Fera charges for AI as a paid add-on' },
      ],
    },
    analytics: {
      complaints: [
        { text: 'You only see star counts, not what customers complain about', count: 47 },
        { text: 'Can’t measure how reviews affect revenue', count: 33 },
      ],
      gatedBy: [
        { text: 'Okendo gates sentiment analytics on its top plan' },
        { text: 'Yotpo Insights is a separate Enterprise plan' },
      ],
    },
    ugcgallery: {
      complaints: [
        { text: 'Great customer photos, but can’t be embedded shoppable', count: 44 },
        { text: 'Galleries are disconnected from reviews', count: 26 },
      ],
      gatedBy: [
        { text: 'Dedicated UGC apps like Foursixty charge $50+/mo' },
        { text: 'Okendo gates shoppable galleries on the Growth plan' },
      ],
    },
    syndication: {
      complaints: [{ text: 'Stars don’t show on Google despite lots of reviews', count: 31 }],
      gatedBy: [
        { text: 'Yotpo gates Google Shopping behind a paid plan' },
        { text: 'Okendo syndication is on the Pro plan' },
      ],
    },
    import: {
      complaints: [
        { text: 'Importing from AliExpress yields language-jumbled reviews', count: 22 },
      ],
      gatedBy: [{ text: 'Ali Reviews charges by number of imported reviews' }],
    },
    widgets: {
      complaints: [{ text: 'Widgets don’t match the theme; you edit CSS by hand', count: 29 }],
      gatedBy: [{ text: 'Loox gates advanced widgets on higher tiers' }],
    },
    reqauto: {
      complaints: [{ text: 'Review-request emails land in spam', count: 18 }],
      gatedBy: [{ text: 'Most gate emails/month by plan' }],
    },
    photovideo: {
      complaints: [{ text: 'Video upload is slow on mobile', count: 15 }],
      gatedBy: [{ text: 'Loox gates video reviews on the Scale plan' }],
    },
  };

  const unmet: UnmetDemand[] = [
    { label: 'Sync reviews to TikTok Shop', count: 142, trend: 'rising' },
    { label: 'Vertical (reels) video reviews embedded on the PDP', count: 98, trend: 'rising' },
    { label: 'Loyalty points for leaving a video review', count: 76, trend: 'rising' },
    { label: 'Auto-detect & flag AI-written reviews', count: 64, trend: 'flat' },
    {
      label: 'Translate multi-language reviews keeping the original voice',
      count: 53,
      trend: 'rising',
    },
  ];

  return { niches, functions, apps, gatedFns, oppEvidence, unmet };
}

// ---------- helpers (ported verbatim from the design) ----------

export function satMeta(s: number) {
  if (s >= 66) return { fg: C.sat, bg: C.satBg, bar: C.satBar, label: 'Saturated' };
  if (s >= 45)
    return { fg: C.contested, bg: C.contestedBg, bar: C.contestedBar, label: 'Contested' };
  return { fg: C.open, bg: C.openBg, bar: C.openBar, label: 'Open' };
}

export function momMeta(m: Momentum) {
  if (m === 'rising') return { fg: '#2c6ecb', arrow: '↑', label: 'Heating up' };
  if (m === 'falling') return { fg: '#6d7175', arrow: '↓', label: 'Cooling' };
  return { fg: '#8c9196', arrow: '→', label: 'Steady' };
}

export function statusMeta(s: FunctionStatus) {
  if (s === 'won') return { fg: C.sat, bg: C.satBg, label: 'Won' };
  if (s === 'contested') return { fg: C.contested, bg: C.contestedBg, label: 'Contested' };
  return { fg: C.open, bg: C.openBg, label: 'Open' };
}

export function oppMeta(s: number) {
  if (s >= 66) return { fg: '#008060', bar: '#29845a', bg: '#e3f1df' };
  if (s >= 45) return { fg: '#b98900', bar: '#e8a33d', bg: '#fcf1cd' };
  return { fg: '#d72c0d', bar: '#e0301e', bg: '#fdeae7' };
}

export interface DepthMeta {
  label: string;
  bg: string;
  bars: [string, string, string];
  scoreFg: string;
}

const DEPTH_MAP: DepthMeta[] = [
  { label: 'None', bg: '#fafafa', bars: ['#e1e3e5', '#e1e3e5', '#e1e3e5'], scoreFg: '#bcbfc4' },
  { label: 'Basic', bg: '#f4f4f5', bars: ['#c4c7cb', '#e1e3e5', '#e1e3e5'], scoreFg: '#8a8a8a' },
  { label: 'Good', bg: '#eceef0', bars: ['#9aa0a6', '#9aa0a6', '#e1e3e5'], scoreFg: '#5b626a' },
  {
    label: 'Excellent',
    bg: '#e2e5e8',
    bars: ['#4a4f57', '#4a4f57', '#4a4f57'],
    scoreFg: '#303030',
  },
];

export function depthMeta(d: number): DepthMeta {
  return DEPTH_MAP[d] ?? DEPTH_MAP[0]!;
}

export function num(n: number): string {
  return n.toLocaleString('en-US');
}

export function numK(n: number): string {
  return n >= 1000 ? (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'k' : '' + n;
}

/** Shared singleton sample dataset. */
export const SAMPLE = buildData();
